<template>
  <div class="moderation-container">
    <p class="title">Moderated Plans</p>
    <div class="search-params">
      <div class="dateFilter">
        From:
        <input type="date" name="search" v-model="dateRange.start" />
        To:
        <input type="date" name="search" v-model="dateRange.end" />
      </div>
      <div class="search-bar field no-margin">
        <span class="icon input-icon">
          <i class="fas fa-search"></i>
        </span>
        <input
          type="text"
          class="input has-icon"
          placeholder="Search ID's"
          v-model="searchTerm"
        />
      </div>
    </div>

    <div class="list__container">
      <div class="list__options">
        <label for="page-size"> Results per page: </label>&nbsp;
        <input
          class="input"
          type="number"
          step="5"
          name="page-size"
          v-model.number="limit"
        />
      </div>
      <div class="list__header">
        <p>C/YP ID</p>
        <p>Service</p>
        <p>Created</p>
        <p>Updated</p>
        <p># Completed</p>
        <p>Differences</p>
      </div>
      <div class="list__content">
        <div
          class="list__content--item"
          role="button"
          v-for="({ plan, audits }, index) in paginatedPlans"
          :key="plan._id"
          @click="expand(index)"

        >
          <div class="collapsed-data">
            <a>{{ plan.internalId }}</a>
            <p>{{plan.service}}</p>
            <p>{{ plan.createdAt | formatDate }}</p>
            <p>{{ plan.updatedAt | formatDate }}</p>
            <span class="status-pill">
              {{ audits.length }}
            </span>
            <p>{{ numberOfDiffs(audits) }}</p>
          </div>
          <div
            class="expanded-data"
            :class="{ 'show-expanded': expandedItem === index }"
          >
            <div class="plan-details" v-if="expandedItem === index">
              <div v-if="viewingAudit.audits.length > 0">
                <div>
                  <div></div>
                  <div
                    class="section-header grades"
                    :class="`outcome-${audit.outcome}`"
                    v-for="audit in viewingAudit.audits"
                    :key="audit._id"
                  >
                    <p class="bold">
                      {{ audit.outcome }}
                      <span class="audited-by">- Audited by: </span>
                      <span class="review-author">{{
                          audit.auditor.name
                        }}</span>
                    </p>
                  </div>
                  <div></div>
                </div>
                <div
                  class="table-data-containers"
                  v-for="section in sections"
                  :key="section"
                >
                  <div class="theme-name"><p>{{ mockSections[section] && mockSections[section].name }}</p></div>
<!--                  <div>Section {{ section.toUpperCase() }}</div>-->
                  <div v-for="audit in viewingAudit.audits" :key="audit._id">
                    <a
                      class="section-result"
                      @click.stop="
                        showResults(audit.sections.find(s => s.id === section))
                      "
                      v-if="
                        audit.sections.find(s => s.id === section)
                          .determination || gradeDescription
                      "
                    >
                      {{
                        audit.sections.find(s => s.id === section)
                          .determination | gradeDescription
                      }}
                    </a>
                  </div>
                  <div class="section-id__diffs">
                    <div class="round" :style="showDiffs(diffs[section])"></div>
                  </div>
                </div>
              </div>
              <div class="empty-state-container" v-else>
                <h3>
                  Results will appear here when audits are completed.
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="list__controls">
        <vue-paginate-al
          :totalPage="totalPages"
          :currentPage="currentPage"
          @btnClick="goToFunction"
          customActiveBGColor="#00b3bb"
          :pageRange="4"
          nextText=">"
          prevText="<"
        >
        </vue-paginate-al>
      </div>
    </div>
    <ModeratedSectionPopup
      :section="sectionToView"
      @close-section-data-popup="showSectionPopup = false"
      v-if="showSectionPopup"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import ModeratedSectionPopup from '@/components/Administrator/Popups/ModeratedSectionPopup.vue'
import { format } from 'date-fns'
import Fuse from 'fuse.js'
import { sections } from '@/mocks/audit'

export default {
  name: 'ModeratedPlans',
  components: { ModeratedSectionPopup },

  data() {
    return {
      searchTerm: '',
      fuse: null,
      searchResults: [],
      limit: 10,
      offset: 0,
      expandedItem: null,
      sectionToView: null,
      showSectionPopup: false,
      lastPage: 0,
      dateRange: {
        start: null,
        end: null
      }
    }
  },
  watch: {
    limit(newValue, oldValue) {
      this.limit = newValue
    }
  },
  computed: {
    ...mapGetters({
      plans: 'plan/plans',
      assignments: 'assignment/assignments',
      audits: 'assignment/audits',
      sections: 'data/sections'
    }),
    totalPages() {
      return Math.ceil(this.moderatedPlans.length / this.limit)
    },
    mockSections() {
      const { g, ...rest } = sections
      return rest
    },
    currentPage() {
      if (this.offset === 0) {
        return 1
      }
      return this.offset / this.limit
    },
    preModeratedPlans() {
      const uniques = new Set()
      const multiples = new Set()
      this.assignments.forEach(assignment => {
        const planId = assignment.planId._id
        if (!uniques.has(planId)) {
          multiples.add(planId)
          uniques.delete(planId)
        } else {
          uniques.add(planId)
        }
      })

      const plans = Array.from(multiples)
        .map(planId => {
          const plan = this.plans.find(plan => plan._id === planId)
          const audits = [...this.audits]
            .filter(audit => audit.outcome && audit.planId._id === planId)
            .slice(0, 3)
          return {
            plan,
            audits
          }
        })
        .filter(a => a.plan)
        .sort((a, b) => (a.plan.createdAt > b.plan.createdAt ? -1 : 1))

      if (!this.dateRange.start || !this.dateRange.end) {
        return plans
      }

      const filteredPlans = (plans || []).filter(x => {
        const targetDate = new Date(x.plan.createdAt).getTime()
        const startDate = new Date(this.dateRange.start).setHours(0, 0, 0, 0)
        const endDate = new Date(this.dateRange.end).setHours(23, 59, 59, 999)
        return targetDate >= startDate && targetDate <= endDate
      })

      return filteredPlans
    },
    moderatedPlans() {
      const fuse = new Fuse(this.preModeratedPlans, {
        includeScore: true,
        threshold: 0.5,
        keys: [
          'plan.ehcpId',
          'plan.internalId'
        ]
      })

      return this.searchTerm.length < 3
        ? this.preModeratedPlans
        : fuse.search(this.searchTerm).map(({ item }) => item)
    },
    paginatedPlans() {
      const limit = this.offset + this.limit
      return this.moderatedPlans.slice(this.offset, limit)
    },
    results() {
      if (!this.searchTerm) {
        return [...this.moderatedPlans].slice(
          this.offset,
          this.offset + this.limit
        )
      }
      return [...this.moderatedPlans]
        .filter(a => this.searchResults.includes(a.planId.ehcpId))
        .slice(this.offset, this.offset + this.limit)
    },
    viewingAudit() {
      if (Number.isNaN(this.expandedItem)) {
        return []
      }
      // Get plan by index and page offset.
      const plan = this.moderatedPlans[this.offset + this.expandedItem]
      plan.audits = plan.audits.slice(0, 3)
      return plan
    },
    diffs() {
      if (Array.isArray(this.viewingAudit)) {
        return []
      }
      return this.sections.reduce((accum, section) => {
        accum = {
          ...accum,
          [section]: []
        }
        for (const audit of this.viewingAudit.audits) {
          audit.sections.forEach(gradedSection => {
            if (gradedSection.id === section) {
              accum[section].push(gradedSection.determination)
            }
          })
        }
        return accum
      }, {})
    },
    fromDate() {
      return new Date(this.dateRange.start)
    },
    toDate() {
      return new Date(this.dateRange.end)
    }
  },
  filters: {
    formatDate(val) {
      if (!val) {
        return ''
      }
      return format(new Date(val), 'PPP')
    }
  },
  methods: {
    expand(item) {
      if (this.expandedItem === item) {
        this.expandedItem = null
        return
      }
      this.expandedItem = item
    },
    showDiffs(diffs) {
      const reducer = (map, val) => {
        if (map[val] == null) {
          map[val] = 1
        } else {
          ++map[val]
        }
        return map
      }
      return Object.values(diffs.reduce(reducer, {})).length > 1
        ? 'background: red'
        : 'background: green'
    },
    numberOfDiffs(audits) {
      const diffs = this.sections.reduce((accum, section) => {
        accum = {
          ...accum,
          [section]: []
        }
        for (const audit of audits) {
          audit.sections.forEach(gradedSection => {
            if (gradedSection.id === section) {
              accum[section].push(gradedSection.determination)
            }
          })
        }
        return accum
      }, {})

      const mapped = Object.values(diffs).map(diff => {
        let count = 0
        diff = diff.length
          ? diff.reduce((accum, val) => {
            if (accum !== val) {
              count++
            }
            return val
          })
          : count
        return count
      })

      return mapped.filter(num => num > 0).length
    },
    showResults(section) {
      this.sectionToView = section
      this.showSectionPopup = true
    },
    goToFunction(value) {
      if (value === this.lastPage) return
      this.offset = (value - 1) * this.limit
      this.lastPage = value
    }
    //,
    // didClickShowAuditData(assignmentId) {
    //   console.log('clicked', assignmentId)
    // }
  }
}
</script>

<style lang="scss" scoped>
@import '@/style/variables.scss';
@import '@/style/components.scss';
@import '@/style/sections.scss';

td,
th {
  height: 3rem;
  text-align: center;
}

tr:nth-child(even) {
  background: #f8f8f8;
}
tr:nth-child(odd) {
  background: #fff;
}

.table-data-container {
  display: flex;
  flex-direction: row;
}

.table-reviews-container {
  display: flex;
  flex-direction: row;
}

.table-review {
  flex-grow: 1;
}

.inner-table-container {
  display: flex;
  flex-direction: row;
  width: 90%;
}

.table-size {
  width: 400px;
  flex-grow: 1;
}

.empty-state-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.round {
  border-radius: 50%;
  width: 22.5px;
  height: 22.5px;
}
.list {
  &__container {
    grid-area: list;
    display: grid;
    grid-template-rows: auto auto 1fr auto;
    position: relative;
    width: 100%;
    height: 100%;
    padding: 1rem;
  }
  &__options {
    justify-self: flex-end;
    .input {
      max-width: 64px;
      padding-left: 1rem;
      font-weight: 600;
    }
  }
  &__header {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    padding: 1rem 0;
    place-items: center;
    font-weight: 600;
    color: darkgrey;
  }
  &__content {
    display: flex;
    justify-content: flex-start;
    width: 100%;
    flex-flow: column;
    &--item {
      display: flex;
      flex-flow: column;
      width: 100%;
      padding: 1rem 0;
      border: 1px solid #eeedec;
      border-radius: 4px;
      box-shadow: none;
      margin-bottom: 1rem;
      transition: box-shadow 200ms;
      &:hover {
        box-shadow: 0px 4px 8px #eeedec;
      }
    }
  }
  &__controls {
    justify-content: space-between;
  }
}

.collapsed-data {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  width: 100%;
  max-height: 58px;
  place-items: center;
}
.expanded-data {
  max-height: 0;
  height: 100%;
  transition: height 200ms ease-in-out;
  width: 100%;
}
.show-expanded {
  max-height: 600px;
  width: 100%;
  overflow-y: scroll;
}

.sections-col {
  width: 10%;
}

.empty-state-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.control {
  display: flex;
  place-items: center;
  place-content: center;
  width: 2rem;
  height: 2rem;
  border: 1px solid #eeedec;
  border-radius: 4px;
  background: transparent;
  box-shadow: none;
  transition: box-shadow 200ms;
  font-weight: 600;
  &:hover {
    box-shadow: 0 4px 8px #eeedec;
  }
}

.plan-details > :first-child {
  margin-top: 0.5rem;
  padding: 1rem;
  > * {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    padding-left: 8px;
    padding-right: 8px;

    > * {
      height: 52px;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    > :not(:first-child):not(:last-child) {
      flex: 1 0 260px;

      a {
        display: block;
        text-align: center;
      }
    }

    > :first-child {
      flex: 0 0 92px;
    }

    > :last-child {
      flex: 0 0 25px;
    }
  }

  > :nth-child(2n) {
    background: whitesmoke;
  }
}
.sections {
  display: flex;
  flex-flow: column;
  align-items: center;
}

.section-header {
  width: 100%;
  text-align: center;
  line-height: 2rem;
  background-color: white;
  color: white;
  margin-top: -0.5rem;
  margin-bottom: 0.5rem;
}

.section-header.grades {
  position: relative;
  color: inherit;
  font-weight: 600;
  text-transform: uppercase;
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    left: 0;
    bottom: 0;
    height: 2px;
  }
  .audited-by {
    text-transform: none;
    font-weight: 400 !important;
  }

  .review-author {
    text-transform: none;
  }
}

.search-params {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  height: 100%;
  width: 100%;
}

.search-bar {
  max-width: 400px;
}

.dateFilter {
  flex-grow: 1;
  flex-shrink: 0;
  padding-right: 0.5rem;
}

.outcome-outstanding::after {
  border: 2px solid $outstanding-color;
}
.outcome-good::after {
  border: 2px solid $good-color;
}
.outcome-requires_improvement::after {
  border: 2px solid $ri-color;
}
.outcome-inadequate::after {
  border: 2px solid $inadequate-color;
}

.key-lookup,
.diffs {
  max-width: 100px;
  background-color: #eeedec50;
}

.list__controls {
  grid-column: 1 / 2;
  justify-content: center;
  margin-bottom: 20px;
}

.list__controls,
.pagination-control {
  display: flex;
}
.pagination-control .control {
  border: none;
}

.link-hover:hover {
  cursor: pointer;
  text-decoration: underline;
}

.current-page {
  color: blue;
  text-decoration: underline;
}
.section-result {
  color: inherit;
  &:hover {
    text-decoration: underline;
  }
}

::v-deep .pagination li a {
  color: black !important;
}

::v-deep .pagination li:hover {
  a {
    text-decoration: underline;
  }
}

::v-deep .pagination .active a {
  color: white !important;
}

.theme-name > p {
  width: 92px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
